import React, { useState, useEffect, useMemo } from 'react';
import copy from 'copy-to-clipboard';
import { v4 as uuidv4 } from 'uuid';

import DemoSlider from './components/DemoSlider';
import Modal from './components/Modal';

import './WebDemo.css';

function trimFirstAndLastCharacter(str) {
  return str.substr(1, str.length - 2);
}

function saveLocalStorage(obj) {
  window.localStorage.setItem('story', JSON.stringify(obj));
}

function getLocalStorage() {
  const { story } = window.localStorage;

  if (!story) {
    return null;
  }

  const parsed = JSON.parse(story);

  return parsed;
}

function processScript(script) {
  const lines = script
    .split('\n')
    .map(x => x.trim())
    .map(x => x.startsWith('label') || x.startsWith('!biglabel') ? null : x)
    .filter(Boolean);

  const processedLines = lines.map((line) => {
    let name = null;
    let text;

    if (line.startsWith('"')) {
      text = trimFirstAndLastCharacter(line);
      return { name, text };
    }

    return {
      name: line.substr(0, line.indexOf(' ')),
      text: trimFirstAndLastCharacter(line.substr(line.indexOf(' ') + 1)),
    };
  });

  return processedLines;
}

function WebDemo() {
  const [sourceCode, setSourceCode] = useState('');
  const [compiledDemo, setCompiledDemo] = useState([]);
  const [seed, setSeed] = useState(0);

  const {
    linesCount,
    wordsCount,
    charactersCount,
    estimateTime,
  } = useMemo(() => {
    const linesCount = compiledDemo.length;
    const wordsCount = compiledDemo
      .map(({ text }) => text.split(' ').length)
      .reduce((acc, cur) => acc + cur, 0);
    const charactersCount = compiledDemo
      .map(({ text }) => text.split('').filter(x => x !== ' ').length)
      .reduce((acc, cur) => acc + cur, 0);
    const estimateTime = ((wordsCount * 0.52) / 60).toFixed(2) + ' minutes';

    return {
      linesCount,
      wordsCount,
      charactersCount,
      estimateTime,
    };
  }, [compiledDemo]);

  const refreshDemo = () => {
    const parsedScript = processScript(sourceCode);

    setCompiledDemo(parsedScript);
    setSeed(Date.now());
  };

  const [storyStorage, setStoryStorage] = useState({ scenes: {}, order: [], hidden: [] });

  const [currentSceneId, setCurrentSceneId] = useState(null);

  const { scenes, order, hidden } = storyStorage;

  const addNewScene = () => {
    const uuid = uuidv4();

    setStoryStorage((prevState) => {
      const newScene = {
        uuid,
        name: 'Unnamed',
        sourceCode: '',
      };

      return {
        ...prevState,
        scenes: {
          ...prevState.scenes,
          [uuid]: newScene,
        },
      };
    });

    return uuid;
  };

  useEffect(() => {
    let story = getLocalStorage();

    if (!story) {
      saveLocalStorage({ scenes: {}, order: [], hidden: [] });
      story = getLocalStorage();
    }

    setStoryStorage(story);

    if (Object.keys(story.scenes).length === 0) {
      const uuid = addNewScene();
      setCurrentSceneId(uuid);
    } else {
      setCurrentSceneId(Object.keys(story.scenes)[0].uuid);
    }
  }, []);

  const saveCurrentScene = () => {
    if (currentSceneId) {
      setStoryStorage((prevState) => ({
        ...prevState,
        scenes: {
          ...prevState.scenes,
          [currentSceneId]: {
            ...prevState.scenes[currentSceneId],
            sourceCode,
          },
        },
      }));
    }
  };

  const selectEdit = (uuid) => {
    saveCurrentScene();
    setCurrentSceneId(uuid);
    setSourceCode(scenes[uuid].sourceCode);
  };

  const moveUp = (uuid) => {
    setStoryStorage((prevState) => {
      const { order } = prevState;

      const index = order.indexOf(uuid);
      if (index === 0) {
        return prevState;
      }

      const newIndex = index - 1;
      const newOrder = [...order];
      const a = order[index]
      const b = order[newIndex];
      newOrder[newIndex] = a;
      newOrder[index] = b;

      return {
        ...prevState,
        order: newOrder,
      };
    });
  };

  const moveDown = (uuid) => {
    setStoryStorage((prevState) => {
      const { order } = prevState;

      const index = order.indexOf(uuid);
      if (index === order.length - 1) {
        return prevState;
      }

      const newIndex = index + 1;
      const newOrder = [...order];
      const a = order[index]
      const b = order[newIndex];
      newOrder[newIndex] = a;
      newOrder[index] = b;

      return {
        ...prevState,
        order: newOrder,
      };
    });
  };

  const moveToUnlisted = (uuid) => {
    setStoryStorage((prevState) => {
      const { order } = prevState;
      const newOrder = order.filter(u => u !== uuid);

      return {
        ...prevState,
        order: newOrder,
      };
    });
  };

  const moveToOrder = (uuid) => {
    setStoryStorage((prevState) => {
      return {
        ...prevState,
        order: [...prevState.order, uuid],
      };
    });
  };

  const saveAll = () => {
    saveCurrentScene();
    saveLocalStorage(storyStorage);
  };

  const exportJsonToClipboard = () => {
    copy(window.localStorage.story);
  }

  const changeCurrentSceneName = (e) => {
    const v = e.target.value;

    setStoryStorage((prevState) => {
      return {
        ...prevState,
        scenes: {
          ...prevState.scenes,
          [currentSceneId]: {
            ...prevState.scenes[currentSceneId],
            name: v,
          },
        },
      };
    });
  };

  const deleteScene = (uuid) => {
    const isConfirm = window.confirm('Do you really want to remove this scene? This action cannot be undone!');

    if (isConfirm) {
      setCurrentSceneId(null);
      setStoryStorage((prevState) => {
        return {
          ...prevState,
          scenes: {
            ...Object.entries(prevState.scenes)
              .filter(([name,]) => name !== uuid)
              .reduce((acc, [name, value]) => {
                acc[name] = value;
                return acc;
              }, {}),
          },
        };
      });
    }
  };

  const refreshTotalDemo = () => {
    const listOfScenes = storyStorage.order
      .map((uuid) => storyStorage.scenes[uuid]);

    let totalScript = '';

    for (const scene of listOfScenes) {
      totalScript += `system "SCENE "${scene.name}"..."\n`;
      totalScript += scene.sourceCode;
      totalScript += '\n';
    }
    const parsedScript = processScript(totalScript);

    setCompiledDemo(parsedScript);
    setSeed(Date.now());
  };

  const [showModal, setShowModal] = useState({
    full: false,
    merge: false,
  });

  const showImportFullJsonModal = (value) => {
    setShowModal((prevState) => ({
      ...prevState,
      full: value,
    }));
  };

  const showImportAndMergeJsonModal = (value) => {
    setShowModal((prevState) => ({
      ...prevState,
      merge: value,
    }));
  };

  const [confirmInput, setConfirmInput] = useState('');

  const [fullJson, setFullJson] = useState('');
  const [mergeJson, setMergeJson] = useState('');

  const confirmFullReplace = () => {
    if (confirmInput.toLowerCase() !== 'i understand') {
      window.alert('Wrong confirmation field');
      return;
    }

    let parsed;
    try {
      parsed = JSON.parse(fullJson);
    } catch (e) {
      window.alert('Invalid JSON!');
      return;
    }

    // sanity check
    // TODO
    if (!parsed.scenes || !parsed.order) {
      window.alert('Sanity check failed');
    }

    setCurrentSceneId(null);
    setStoryStorage(parsed);
    setConfirmInput('');
    setFullJson('');
    showImportFullJsonModal(false);
  };

  const confirmMergeAdd = () => {
    let parsed;
    try {
      parsed = JSON.parse(mergeJson);
    } catch (e) {
      window.alert('Invalid JSON!');
      return;
    }

    setStoryStorage((prevState) => {
      const { scenes } = prevState;

      const newScenes = {};

      Object.keys(parsed.scenes).forEach((key) => {
        if (scenes[key]) {
          console.log('Duplicate scene', { sceneId: key, scene: scenes[key] });
        } else {
          newScenes[key] = parsed.scenes[key];
        }
      });

      return {
        ...prevState,
        scenes: {
          ...prevState.scenes,
          ...newScenes,
        },
      };
    });
    setMergeJson('');
    showImportAndMergeJsonModal(false);
  };

  return (
    <div className="web-demo-wrapper">
      <div className="web-demo">
        <div className="left-demo">
          <DemoSlider slides={compiledDemo} seed={seed} />
        </div>

        <div className="right-code-editor">
          <div className="stats">
            <div className="stat-line">
              Lines: {linesCount}
            </div>
            <div className="stat-line">
              Words: {wordsCount}
            </div>
            <div className="stat-line">
              Characters: {charactersCount}
            </div>
            <div className="stat-line">
              Est. Time: {estimateTime}
            </div>
          </div>

          <div className="code-editor-wrapper">
            <textarea
              className="editor"
              onChange={(e) => setSourceCode(e.target.value)}
              value={sourceCode}
            />
          </div>

          <div className="button-wrapper">
            <div>Currently editing: {currentSceneId ? scenes[currentSceneId].name : 'Please select scene to edit'}</div>
            {Boolean(currentSceneId) && (
              <div>
                Edit name:
                <input
                  placeholder="Name"
                  onChange={changeCurrentSceneName}
                  value={scenes[currentSceneId].name}
                />
              </div>
            )}

            <button onClick={refreshDemo}>
              Refresh demo
            </button>
          </div>
        </div>
      </div>

      <div className="story-board">
        <div className="story-builder">
          <div className="story-main-list">
            {order.map((uuid) => (
              <div className="ord-item">
                <div className="ord-item-name">
                  {scenes[uuid].name}
                </div>
                <div className="ord-item-buttons">
                  <button className="button-ord-item" onClick={() => selectEdit(uuid)} title="Edit">✏️</button>
                  <button className="button-ord-item" onClick={() => moveUp(uuid)}>↑</button>
                  <button className="button-ord-item" onClick={() => moveDown(uuid)}>↓</button>
                  <button className="button-ord-item" onClick={() => moveToUnlisted(uuid)}>→</button>
                </div>
              </div>
            ))}
          </div>
          <div className="story-unlisted">
            <div className="story-unlisted-list">
              {Object.values(scenes).filter(({ uuid }) => !order.includes(uuid)).map(({ name, uuid }) => (
                <div className="ord-item">
                  <div className="ord-item-name">{name}</div>
                  <div className="ord-item-buttons">
                    <button className="button-ord-item" onClick={() => selectEdit(uuid)}>✏️</button>
                    <button className="button-ord-item" onClick={() => moveToOrder(uuid)}>←</button>
                    <button className="button-ord-item" onClick={() => deleteScene(uuid)}>❌</button>
                  </div>
                </div>
              ))}
            </div>
            <div className="buttons-unlisted-wrapper">
              <div className="warning">
                Selecting "Edit" on another scene will save your current scene!
              </div>

              <button onClick={() => saveAll()}>Save all</button>

              <button onClick={() => addNewScene()}>Add new scene</button>

              <button onClick={() => exportJsonToClipboard()}>Export JSON to clipboard</button>

              <button onClick={() => refreshTotalDemo()}>Compile full demo</button>

              <button onClick={() => showImportFullJsonModal(true)}>Import full JSON</button>
              <button onClick={() => showImportAndMergeJsonModal(true)}>Import JSON and merge</button>
            </div>
          </div>
        </div>

        <div className="bruh" />
      </div>

      <Modal isShown={showModal.full} onClose={() => showImportFullJsonModal(false)}>
        <div className="danger-title">DANGER! DANGER! DANGER!</div>
        <div>
          This action will <b>COMPLETELY OVERWRITE YOUR CURRENT DATA</b>. If you want to keep you data, export your current work as JSON or use Merge Tool.
        </div>

        <div>
          Whatever you are trying to do, MAKE A BACKUP.
        </div>

        <div>
          Don't forget to "save" later.
        </div>

        <textarea
          className="modal-textarea"
          value={fullJson}
          onChange={(e) => setFullJson(e.target.value)}
        />

        <div>To proceed, type "I UNDERSTAND" in field below:</div>

        <input onChange={(e) => setConfirmInput(e.target.value)} value={confirmInput} />

        <div className="confirm-button-wrapper">
          <button className="danger-button" onClick={confirmFullReplace}>Confirm</button>
        </div>
      </Modal>

      <Modal isShown={showModal.merge} onClose={() => showImportAndMergeJsonModal(false)}>
        <div>
          All scenes with non-conflicting UUIDs will be added to list. All scenes that already exist will be ignored.
        </div>

        <textarea
          className="modal-textarea"
          value={mergeJson}
          onChange={(e) => setMergeJson(e.target.value)}
        />

        <div className="confirm-button-wrapper">
          <button onClick={confirmMergeAdd}>Confirm</button>
        </div>
      </Modal>
    </div>
  );
}

export default WebDemo;
